import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Clock, CheckCircle, Phone, Mail, Globe, ArrowRight } from 'lucide-react';

interface FinalCtaSectionProps {
  companyName?: string;
  onCtaClick: () => void;
}

export const FinalCtaSection: React.FC<FinalCtaSectionProps> = ({ 
  companyName = "votre entreprise", 
  onCtaClick 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    phone: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCtaClick();
  };

  const urgencyFeatures = [
    "Audit complet en 48h",
    "Plan d'action personnalisé",
    "Consultation stratégique offerte",
    "Garantie résultats 90 jours"
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-white/5 to-orange-800/10 rounded-full blur-3xl"></div>
      </div>

      {/* Animated Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          
          {/* Urgency Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/20 border border-white/30 text-white text-sm font-medium mb-6">
              <Clock className="w-4 h-4 mr-2 animate-pulse" />
              Offre Limitée - Ce Mois-ci Uniquement
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Prêt à multiplier votre trafic par 3 ?
            </h2>
            
            <p className="text-xl text-orange-100 max-w-3xl mx-auto leading-relaxed mb-8">
              Rejoignez les entreprises suisses qui ont transformé leur présence digitale. 
              <span className="font-semibold text-white"> Places limitées ce mois-ci.</span>
            </p>

            {/* Countdown Timer Effect */}
            <div className="inline-flex items-center space-x-4 bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">15</div>
                <div className="text-orange-100 text-xs">Places</div>
              </div>
              <div className="w-px h-8 bg-white/30"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">7</div>
                <div className="text-orange-100 text-xs">Jours</div>
              </div>
              <div className="w-px h-8 bg-white/30"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white animate-pulse">⏰</div>
                <div className="text-orange-100 text-xs">Restants</div>
              </div>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Column - Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Analyse Gratuite de {companyName}
                  </h3>
                  <p className="text-gray-600">
                    Obtenez votre audit personnalisé en 5 minutes
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                      placeholder="Votre nom"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email professionnel *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                      placeholder="votre@email.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site web *
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                      placeholder="https://votre-site.ch"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                      placeholder="+41 XX XXX XX XX"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center group"
                  >
                    <Target className="w-5 h-5 mr-2" />
                    Obtenir mon audit gratuit
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>

                  <div className="text-center text-xs text-gray-500 mt-4">
                    🔒 Vos données sont sécurisées et ne seront jamais partagées
                  </div>
                </form>
              </div>
            </motion.div>

            {/* Right Column - Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-4">
                  Ce que vous recevez gratuitement :
                </h3>
                
                <div className="space-y-4">
                  {urgencyFeatures.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center"
                    >
                      <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-lg">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <h4 className="font-bold text-lg mb-4">Besoin d'aide ? Contactez-nous :</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 mr-3" />
                    <span>+41 XX XXX XX XX</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 mr-3" />
                    <span>contact@smart-impact.ch</span>
                  </div>
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 mr-3" />
                    <span>Suisse • France • Belgique</span>
                  </div>
                </div>
              </div>

              {/* Guarantee */}
              <div className="mt-8 text-center">
                <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/20 border border-white/30 backdrop-blur-sm">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Garantie résultats ou remboursé</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom Trust Bar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">5 min</div>
                  <div className="text-orange-100 text-sm">Temps d'analyse</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">48h</div>
                  <div className="text-orange-100 text-sm">Rapport détaillé</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">90j</div>
                  <div className="text-orange-100 text-sm">Garantie résultats</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">100%</div>
                  <div className="text-orange-100 text-sm">Gratuit</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FinalCtaSection;
